import {AnimatePresence, motion} from 'framer-motion';
import {
  DataStatus,
  DropdownItem,
  FormControl,
  FormField,
  Label,
  openDropdown,
  showNotification,
} from 'platform/components';
import {HStack, Link, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {
  ContextTemplateResponseBody,
  useGetContextTemplateListQuery,
  useRenderServiceOrderEmailTemplateMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {myProfileRoutes} from '@dms/routes';
import {
  type EmailFormValues as FormValues,
  handleApiError,
  replaceVariables,
  VARIABLE_REGEX,
} from '@dms/shared';

interface MessageFormFieldProps {
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
  serviceCaseId: string;
  serviceOrderId?: string;
}

export function MessageFormField(props: MessageFormFieldProps) {
  const [renderServiceOrderTemplate, {isLoading: isRenderServiceOrderTemplateInProgress}] =
    useRenderServiceOrderEmailTemplateMutation();

  const customerMessageId = useWatch({
    control: props.control,
    name: 'customerMessageId',
  });

  const {
    data: templates,
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useGetContextTemplateListQuery({
    type: 'email',
    context: 'service-order',
  });

  const onTemplateClick = (template: ContextTemplateResponseBody) => {
    if (!props.serviceOrderId) {
      showNotification.error();
      return;
    }

    renderServiceOrderTemplate({
      renderServiceOrderEmailTemplateRequestBody: {
        template: {
          subject: template.subject ?? '',
          body: template.body,
        },
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
      },
    })
      .unwrap()
      .then((response) => {
        props.formApi.setValue('subject', replaceVariables(response.subject));
        props.formApi.setValue('body', replaceVariables(response.body));
        if (VARIABLE_REGEX.test(response.subject) || VARIABLE_REGEX.test(response.body)) {
          showNotification.warning(i18n.t('general.notifications.notAllPlaceholdersFilled'));
        }
      })
      .catch(handleApiError);
  };

  const isCustomerMessage = customerMessageId?.includes('customer-message') ?? false;

  return (
    <AnimatePresence initial={false} mode="wait">
      {isCustomerMessage && (
        <motion.div
          initial={{height: 0, opacity: 0}}
          animate={{height: 'auto', opacity: 1}}
          exit={{height: 0, opacity: 0}}
          transition={{duration: 0.2, ease: 'easeInOut'}}
          style={{overflow: 'hidden'}}
        >
          <VStack spacing={4}>
            <FormField
              control={props.control}
              name="subject"
              type="text"
              label={i18n.t('general.labels.subject')}
              isRequired={isCustomerMessage}
            />

            <VStack spacing={1}>
              <FormField
                control={props.control}
                name="body"
                type="textarea"
                isDisabled={isRenderServiceOrderTemplateInProgress}
                label={i18n.t('general.labels.message')}
                labelActions={
                  props.serviceOrderId
                    ? [
                        {
                          title: i18n.t('entity.customer.labels.chooseEmailTemplate'),
                          leftIcon: 'action/autorenew',
                          isDisabled: isRenderServiceOrderTemplateInProgress,
                          onClick: (event) =>
                            openDropdown(
                              event,
                              <>
                                <DataStatus
                                  isError={isTemplatesError}
                                  isLoading={isLoadingTemplates}
                                />
                                {templates?.map((template) => (
                                  <DropdownItem
                                    label={template.name}
                                    key={template.id}
                                    onClick={() => onTemplateClick(template)}
                                  />
                                ))}
                              </>,
                              {
                                isHeightLimited: true,
                              }
                            ),
                        },
                      ]
                    : []
                }
                isRequired={isCustomerMessage}
                isScrollable
                rows={7}
              />
              <HStack justify="space-between" width="100%">
                <Label>{i18n.t('entity.customer.labels.messageWithSignature')}</Label>
                <Link
                  leftIcon="image/edit"
                  size="xSmall"
                  href={myProfileRoutes.emailSignature}
                  target="_blank"
                >
                  {i18n.t('entity.customer.labels.goToUserSignature')}
                </Link>
              </HStack>
            </VStack>
          </VStack>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
